import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { ref } from 'vue';
import { setErrorNotification } from '~~/common/utils/helper';

import { useNotificationStore } from '~~/common/stores/notification';
import { useUserStore } from '~~/common/stores/user';
import { useOrderStore } from '~~/common/stores/order';
import type { ResponseDataOpStrict, Schema } from '~~/common/types/api';
import { isAxiosError } from 'axios';
import type { AxiosError } from 'axios';

type LoyaltyCardType = Schema<'LoyaltyCardModel'>;
type LoyaltyCardLevelType = Schema<'LoyaltyCardLevelModel'>;
type ExpectedLoyaltyPointsType = Schema<'LoyaltyCardModel.ExpectedLoyaltyPointsModel'>;
type LoyaltyCouponsType = Schema<'MeModel.LoyaltyCouponModel'>;
type LoyaltyStickersType = Schema<'MeModel.LoyaltyStickerModel'>;
type LoyaltyStickerDetailsType = Schema<'MeModel.LoyaltyStickerDetailsModel'>;
type LoyaltyCardSettingsType = Schema<'LoyaltyCardSettingsModel'>;
type LoyaltyTransactionType = Schema<'MeModel.LoyaltyTransactionModel'>;

type LoyaltyCouponProductsResponseType = ResponseDataOpStrict<'get_loyalty_coupon_products'>;

export const useLoyaltyCardStore = defineStore('loyaltyCard', () => {
  const nuxtApp = useNuxtApp();

  const notificationStore = useNotificationStore();

  const loyaltyCard = ref<LoyaltyCardType | null>(null);
  const loyaltyCardLevel = ref<LoyaltyCardLevelType | null>(null);
  const loyaltyCardLevelInfo = ref<LoyaltyCardLevelType[] | null>(null);
  const expectedLoyaltyPoints = ref<ExpectedLoyaltyPointsType | null>(null);

  const loyaltyCoupons = ref<LoyaltyCouponsType[]>([]);
  const loyaltyCouponProducts = ref<Record<number, LoyaltyCouponProductsResponseType>>({});
  // A sticker slot may hold an empty placeholder when the details request failed
  // (see `fetchLoyaltyStickerById`'s finally fallback) — the API itself 404s rather
  // than returning an empty object, so `{}` only ever comes from the FE fallback.
  const loyaltySticker = ref<Record<number, LoyaltyStickerDetailsType | Record<string, never>>>({});
  const loyaltyStickers = ref<LoyaltyStickersType[]>([]);
  const loyaltyCardSettings = ref<LoyaltyCardSettingsType | null>(null);

  const visibleBottomBlock = ref(true);
  const expectedLoyaltyPointsKey = computed(() => useUserStore().data?.id && useOrderStore().order);

  function resetLoyaltyState () {
    loyaltyCard.value = null;
    expectedLoyaltyPoints.value = null;
    loyaltyCoupons.value = [];
    loyaltyCouponProducts.value = {};
    loyaltySticker.value = {};
    loyaltyStickers.value = [];
    loyaltyCardSettings.value = null;
  }

  function resetLoyaltyData () {
    if (__AW_IS_SHOP__) {
      resetLoyaltyState();
    }
  }

  function setLoyaltyCard (newLoyaltyCard: LoyaltyCardType | null) {
    if (__AW_IS_SHOP__) {
      loyaltyCard.value = newLoyaltyCard;
    }
  }

  function setLoyaltyCardLevel (newLoyaltyCardLevel: LoyaltyCardLevelType | null) {
    if (__AW_IS_SHOP__) {
      loyaltyCardLevel.value = newLoyaltyCardLevel;
    }
  }

  function setLoyaltyCardLevelInfo (newLoyaltyCardLevelInfo: LoyaltyCardLevelType[] | null) {
    if (__AW_IS_SHOP__) {
      loyaltyCardLevelInfo.value = newLoyaltyCardLevelInfo;
    }
  }

  function setExpectedLoyaltyPoints (newExpectedLoyaltyPoints: ExpectedLoyaltyPointsType | null) {
    if (__AW_IS_SHOP__) {
      expectedLoyaltyPoints.value = newExpectedLoyaltyPoints;
    }
  }

  function setLoyaltyCoupons (payload: LoyaltyCouponsType[]) {
    if (__AW_IS_SHOP__) {
      loyaltyCoupons.value = payload;
    }
  }

  function setLoyaltyCouponProductsByCouponId (couponId: number, payload: LoyaltyCouponProductsResponseType, append = false) {
    if (__AW_IS_SHOP__) {
      if (append && loyaltyCouponProducts.value[couponId]) {
        loyaltyCouponProducts.value[couponId] = {
          ...payload,
          results: [...loyaltyCouponProducts.value[couponId].results, ...payload.results],
        };
      } else {
        loyaltyCouponProducts.value[couponId] = payload;
      }
    }
  }


  function setLoyaltySticker (payload: LoyaltyStickerDetailsType | Record<string, never>, id: number) {
    if (__AW_IS_SHOP__) {
      loyaltySticker.value[id] = payload;
    }
  }

  function setLoyaltyStickers (payload: LoyaltyStickersType[]) {
    if (__AW_IS_SHOP__) {
      loyaltyStickers.value = payload;
    }
  }

  function setLoyaltyCardSettings (settings: LoyaltyCardSettingsType | null) {
    if (__AW_IS_SHOP__) {
      loyaltyCardSettings.value = settings;
    }
  }

  async function fetchLoyaltyCard () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        const result = await $api.$get<LoyaltyCardType>('/loyalty_card/current');
        setLoyaltyCard(result);
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;

        // if error.response.status === 404 we don't have loyalty card, this is not error
        if (error?.response?.status === 404) {
          setLoyaltyCard(null);
        } else {
          $logger.error(error);
          await setErrorNotification({ nuxtApp, error });
        }
      }
    }
  }

  async function fetchLoyaltyCardLevel () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        const result = await $api.$get<LoyaltyCardLevelType>('/loyalty_card/current/level');
        setLoyaltyCardLevel(result);
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        // if error.response.status === 404 we don't have loyalty card, this is not error
        if (error?.response?.status === 404) {
          setLoyaltyCardLevel(null);
        } else {
          $logger.error(error);
          await setErrorNotification({ nuxtApp, error });
        }
      }
    }
  }

  async function fetchLoyaltyCardLevelInfo () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        const result = await $api.$get<LoyaltyCardLevelType[]>('/loyalty_card_level_info');
        setLoyaltyCardLevelInfo(result);
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        // if error.response.status === 404 we don't have loyalty card, this is not error
        if (error?.response?.status === 404) {
          setLoyaltyCardLevelInfo(null);
        } else {
          $logger.error(error);
          await setErrorNotification({ nuxtApp, error });
        }
      }
    }
  }

  async function createLoyaltyCard () {
    const {
      $api,
      $logger,
    } = nuxtApp;

    if (__AW_IS_SHOP__) {
      try {
        const result = await $api.$post<LoyaltyCardType>('/loyalty_card', {});
        setLoyaltyCard(result);
        notificationStore.pushSuccess({
          manualClose: false,
          text: {
            titleToken: 'aw.profile.loyalty_card.card_info.success_message',
          },
        });
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    }
  }

  async function deleteLoyaltyCard () {
    const { $api } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        await $api.delete('/loyalty_card/current');
        await fetchLoyaltyCard();
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        await setErrorNotification({ nuxtApp, error });
      }
    }
  }

  async function fetchExpectedLoyaltyPoints ({
    id = 'current',
    type = 'ALL',
  }) {
    const {
      $api,
      $logger,
    } = nuxtApp;

    if (__AW_IS_SHOP__) {
      const userStore = useUserStore();
      // todo csak akkor kérjük ezt le, ha a usernek van loyalty cardja
      if (userStore.isEcomUser) {
        try {
          const result = await $api.$get<ExpectedLoyaltyPointsType>(`/orders/${id}/cart/${type}/expected_loyalty_points`);
          setExpectedLoyaltyPoints(result);
        } catch (e) {
          const error = e as AxiosError<Schema<'ErrorModel'>>;
          // if error.response.status === 404 we don't have loyalty card, this is not error
          if (error?.response?.status === 404) {
            setExpectedLoyaltyPoints(null);
          } else {
            $logger.error(error);
            await setErrorNotification({ nuxtApp, error });
          }
        }
      } else {
        setExpectedLoyaltyPoints(null);
      }
    }
  }

  async function fetchLoyaltyCoupons () {
    const {
      $api,
      $logger,
    } = nuxtApp;

    if (__AW_IS_SHOP__) {
      let resp: undefined | LoyaltyCouponsType[];
      try {
        resp = await $api.$get<LoyaltyCouponsType[]>('me/loyalty/coupons');
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        if (error?.response?.status !== 404) {
          $logger.error(error);
          setErrorNotification({ nuxtApp, error });
        }
      } finally {
        if (!resp || typeof resp !== 'object') {
          resp = [];
        }
      }
      setLoyaltyCoupons(resp);
    }
  }

  async function fetchLoyaltyCouponProductsByCouponId (couponId: number, page = 1) {
    const {
      $api,
      $logger,
    } = nuxtApp;

    if (__AW_IS_SHOP__) {
      let resp: undefined | LoyaltyCouponProductsResponseType;
      try {
        resp = await $api.$get<LoyaltyCouponProductsResponseType>(`/me/loyalty/coupons/${couponId}/products?itemsPerPage=6&page=${page}`);
      } catch (e) {
        if (isAxiosError(e) && e.response?.status !== 404) {
          $logger.error(e);
          setErrorNotification({ nuxtApp, error: e });
        }
      } finally {
        if (!resp || typeof resp !== 'object') {
          resp = {
            itemCount: 0,
            results: [],
            pageCount: 0,
            currentPage: 0,
          };
        }
      }
      setLoyaltyCouponProductsByCouponId(couponId, resp, page > 1);
    }
  }

  async function activateLoyaltyCoupon ({ id }: { id: number }) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        await $api.$post('me/loyalty/coupon/activate', { id });
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      } finally {
        fetchLoyaltyCoupons();
      }
    }
  }

  async function fetchLoyaltyStickerById (id: number) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      let result: LoyaltyStickerDetailsType | Record<string, never> | undefined;
      try {
        result = await $api.$get<LoyaltyStickerDetailsType>(`me/loyalty/sticker/${id}/details`);
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        if (error?.response?.status !== 404) {
          $logger.error(error);
          setErrorNotification({ nuxtApp, error });
        }
      } finally {
        if (!result || typeof result !== 'object') {
          result = {};
        }
      }
      setLoyaltySticker(result, id);
    }
  }

  async function fetchLoyaltyStickers () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      let resp: undefined | LoyaltyStickersType[];
      try {
        resp = await $api.$get<LoyaltyStickersType[]>('me/loyalty/stickers');
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        if (error?.response?.status !== 404) {
          $logger.error(error);
          setErrorNotification({ nuxtApp, error });
        }
      } finally {
        if (!resp || typeof resp !== 'object') {
          resp = [];
        }
      }
      setLoyaltyStickers(resp);
    }
  }

  async function fetchLoyaltyTransactions ({
    page,
    count,
  }: {
    page: number;
    count: number;
  }) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      let resp: LoyaltyTransactionType[] | null = null;
      try {
        resp = await $api.$get<LoyaltyTransactionType[]>(`me/loyalty/transactions?page=${page || 1}&itemsPerPage=${count || 10}`);
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        if (error?.response?.status !== 404) {
          $logger.error(error);
          setErrorNotification({ nuxtApp, error });
        }
      } finally {
        if (!resp || !Array.isArray(resp)) {
          resp = [];
        }
      }

      return resp;
    }
    return null;
  }

  async function fetchLoyaltyCardSettings () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    if (__AW_IS_SHOP__) {
      try {
        const result = await $api.$get<LoyaltyCardSettingsType>('/loyalty_card/current/settings');
        setLoyaltyCardSettings(result);
      } catch (e) {
        const error = e as AxiosError<Schema<'ErrorModel'>>;
        // if e.response.status === 404 we don't have loyalty card, this is not error
        if (error?.response?.status === 404) {
          setLoyaltyCardSettings(null);
        } else {
          $logger.error(error);
          await setErrorNotification({ nuxtApp, error });
        }
      }
    }
  }

  function hideBottomBlock () {
    visibleBottomBlock.value = false;
  }

  function showBottomBlock () {
    visibleBottomBlock.value = true;
  }

  return {
    loyaltyCard,
    loyaltyCardLevel,
    loyaltyCardLevelInfo,
    expectedLoyaltyPoints,
    expectedLoyaltyPointsKey,
    loyaltyCoupons,
    loyaltyCouponProducts,
    loyaltySticker,
    loyaltyStickers,
    loyaltyCardSettings,
    visibleBottomBlock,
    resetLoyaltyData,
    setLoyaltyCard,
    setLoyaltyCardLevel,
    setLoyaltyCardLevelInfo,
    setLoyaltyCoupons,
    setLoyaltySticker,
    setLoyaltyStickers,
    fetchLoyaltyCard,
    fetchLoyaltyCardLevel,
    fetchLoyaltyCardLevelInfo,
    createLoyaltyCard,
    deleteLoyaltyCard,
    fetchExpectedLoyaltyPoints,
    fetchLoyaltyCoupons,
    activateLoyaltyCoupon,
    fetchLoyaltyStickerById,
    fetchLoyaltyStickers,
    fetchLoyaltyTransactions,
    fetchLoyaltyCardSettings,
    hideBottomBlock,
    showBottomBlock,
    setLoyaltyCouponProductsByCouponId,
    fetchLoyaltyCouponProductsByCouponId,
  };
});
